import {
  faCaretLeft,
  faCaretRight,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  NavLink,
  Outlet,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Button } from "../components/button";
import { ButtonGroup } from "../components/button-group";
import { TimeHeader } from "../components/time/header";
import { TimeList } from "../components/time/list";
import { TimeWeek } from "../components/time/week";
import { useTimes } from "../queries/time";
import { useLocale } from "../stores/locale";
import { dateFormat } from "../utils/date-format";

export function Time() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const queryClient = useQueryClient();
  const t = useLocale((state) => state.locale);

  useEffect(() => {
    if (dateParam === null) {
      navigate(`?date=${dateFormat(new Date())}`);
    }
  }, [dateParam, navigate]);

  const date = dateParam ? new Date(dateParam) : new Date();

  const { data: times } = useTimes({
    date,
  });

  return (
    <>
      <Outlet />
      <div className="flex gap-4 mb-4">
        <Button route={`/time/create?date=${dateFormat(date)}`} size="lg">
          <FontAwesomeIcon icon={faPlus} />
        </Button>
        <ButtonGroup>
          <Button
            route={`/time/?date=${dateFormat(previousDay(date))}`}
            size="lg"
          >
            <FontAwesomeIcon icon={faCaretLeft} />
          </Button>
          <Button route={`/time/?date=${dateFormat(new Date())}`} size="lg">
            {t.time.today}
          </Button>
          <Button route={`/time/?date=${dateFormat(nextDay(date))}`} size="lg">
            <FontAwesomeIcon icon={faCaretRight} />
          </Button>
        </ButtonGroup>
        <TimeHeader date={new Date(date)} />
      </div>
      <div className="hidden sm:block">
        <TimeWeek date={date} />
      </div>
      {!times ? (
        <div className="border-b border-gray-200 bg-gray-100 h-24 flex items-center justify-center">
          <p className="italic">{t.loading}</p>
        </div>
      ) : (
        <TimeList
          date={date}
          queryParamForEditLink={`date=${dateParam}`}
          times={times}
          onListChange={() => {
            queryClient.invalidateQueries({
              queryKey: ["get-times", date],
            });
          }}
        />
      )}
    </>
  );
}

function nextDay(date: Date): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 1);
  return newDate;
}
function previousDay(date: Date): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - 1);
  return newDate;
}
